import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import fr from './fr';
import cr from './cr';

const translations = { fr, cr };

const LanguageContext = createContext(null);

// Available languages
export const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'cr', name: 'Kréol Rénioné', flag: '🏝️' }
];

export const LanguageProvider = ({ children }) => {
  // Get initial language from localStorage or default to French
  const [language, setLanguageState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('bourbon_language') || 'fr';
    }
    return 'fr';
  });

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('bourbon_language', language);
    // Update HTML lang attribute
    document.documentElement.lang = language === 'cr' ? 'cpf' : 'fr';
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  }, []);

  // Get nested translation by key path (e.g., 'common.loading')
  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to French if key not found in current language
        value = translations['fr'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found anywhere
          }
        }
        break;
      }
    }

    // Handle string interpolation
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }

    return typeof value === 'string' ? value : key;
  }, [language]);

  // Get current language info
  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    t,
    currentLanguage,
    languages: LANGUAGES
  }), [language, t, currentLanguage]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Hook for formatted relative time
export const useRelativeTime = () => {
  const { t } = useLanguage();

  return useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMins < 1) return t('time.now');
    if (diffMins < 60) return t('time.minutesAgo', { n: diffMins });
    if (diffHours < 24) return t('time.hoursAgo', { n: diffHours });
    if (diffDays < 7) return t('time.daysAgo', { n: diffDays });
    if (diffWeeks < 4) return t('time.weeksAgo', { n: diffWeeks });
    if (diffMonths < 12) return t('time.monthsAgo', { n: diffMonths });
    return t('time.yearsAgo', { n: diffYears });
  }, [t]);
};

export default LanguageContext;
